import type { MinimalAsset } from '../../app/AssetsHelpGang.tsx';
import { marketplaceApi } from '../../../services/marketplaceApi.ts';
import type { AccountWithAssets } from '../../app/assets/dialogs/contractDialogUtils';
import type { AppDispatch } from '../../../configuration/setup/store.ts';

const hasContracts = async (dispatch: AppDispatch, accountWithAssets: AccountWithAssets): Promise<boolean> => {
    try {
        const response = await dispatch(
            marketplaceApi.endpoints.getContracts.initiate(
                { accountId: accountWithAssets.accountId, assetIds: accountWithAssets.assetIds },
                { forceRefetch: true }
            )
        ).unwrap();
        return response.length > 0;
    } catch (err) {
        console.error(`Could not resolve contracts for account ${accountWithAssets.accountId}:`, err);
        throw err;
    }
};

export const getAccountsWithContracts = async (
    dispatch: AppDispatch,
    assetsToArchive: MinimalAsset[]
): Promise<AccountWithAssets[]> => {
    if (assetsToArchive.length > 100) {
        throw Error('TOO_MANY_ASSETS_SELECTED');
    }

    const accountsWithAssets = groupAssetsByAccount(assetsToArchive);
    const accountsWithContractsInformation: {
        accountWithAssets: AccountWithAssets;
        hasContracts: boolean;
    }[] = await Promise.all(
        accountsWithAssets.map(async accountWithAssets => ({
            accountWithAssets,
            hasContracts: await hasContracts(dispatch, accountWithAssets),
        }))
    );

    return accountsWithContractsInformation
        .filter(({ hasContracts }) => hasContracts)
        .map(({ accountWithAssets }) => accountWithAssets);
};

const groupAssetsByAccount = (assets: MinimalAsset[]): AccountWithAssets[] => {
    const grouped = new Map<string, string[]>();

    for (const { accountId, id } of assets) {
        if (!grouped.has(accountId)) {
            grouped.set(accountId, []);
        }
        // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
        grouped.get(accountId)!.push(id);
    }

    return Array.from(grouped, ([accountId, assetIds]) => ({ accountId, assetIds }));
};
